import React, { useState } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { contactInfo } from "../mock";
import { Phone, Mail, MapPin, Clock, Send, Loader2 } from "lucide-react";
import { useToast } from "../hooks/use-toast";

export default function Contact() {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const submit = (e) => {
    e.preventDefault();
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      toast({ title: "Message sent", description: "We will get back to you shortly." });
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    }, 1000);
  };

  return (
    <>
      <Header />
      <section
        className="h-[42vh] min-h-[280px] w-full bg-cover bg-center relative flex items-end"
        style={{
          backgroundImage:
            "linear-gradient(rgba(10,20,40,0.85), rgba(10,20,40,0.85)), url('https://images.pexels.com/photos/6077296/pexels-photo-6077296.jpeg?auto=compress&cs=tinysrgb&w=1920')",
        }}
      >
        <div className="container-x pb-14 text-white">
          <span className="text-[#c9a961] uppercase tracking-[0.35em] text-xs font-semibold">Reach Out</span>
          <h1 className="font-serif-display text-4xl md:text-6xl mt-3">Contact Us</h1>
          <div className="text-sm text-neutral-300 mt-3">
            <Link to="/" className="hover:text-[#c9a961]">Home</Link> <span className="mx-2">/</span> Contact
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="container-x grid grid-cols-1 lg:grid-cols-3 gap-10">
          {[
            { icon: MapPin, title: "Office Address", body: contactInfo.address },
            { icon: Phone, title: "Call Us", body: contactInfo.phone },
            { icon: Mail, title: "Email", body: `${contactInfo.email} | ${contactInfo.altEmail}` },
          ].map((c, i) => {
            const Icon = c.icon;
            return (
              <div key={i} className="bg-white border border-neutral-200 rounded-sm p-8 text-center hover:border-[#c9a961] transition-colors">
                <div className="w-14 h-14 rounded-sm bg-[#0e1b34] text-[#c9a961] flex items-center justify-center mx-auto mb-4">
                  <Icon size={22} />
                </div>
                <h3 className="font-serif-display text-xl text-[#0e1b34] mb-2">{c.title}</h3>
                <p className="text-neutral-600 text-sm leading-relaxed">{c.body}</p>
              </div>
            );
          })}
        </div>

        <div className="container-x mt-16 grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
          <div>
            <span className="text-[#b28e42] uppercase tracking-[0.35em] text-xs font-semibold">Send a Message</span>
            <h2 className="font-serif-display text-4xl text-[#0e1b34] mt-3 mb-6 gold-underline">Get In Touch</h2>
            <p className="text-neutral-600 mb-6">Have a question or want to schedule a consultation? Fill out the form and our team will get back within one business day.</p>
            <ul className="space-y-3">
              <li className="flex items-center gap-3 text-neutral-700"><Clock className="text-[#c9a961]" size={18} /> Monday - Saturday, 10 AM - 10 PM</li>
              <li className="flex items-center gap-3 text-neutral-700"><Clock className="text-[#c9a961]" size={18} /> Sunday, 10 AM - 5 PM</li>
            </ul>
          </div>

          <form onSubmit={submit} className="bg-white border border-neutral-200 rounded-sm p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input required name="name" value={form.name} onChange={onChange} placeholder="Full Name" className="input-plain" />
              <input required name="email" type="email" value={form.email} onChange={onChange} placeholder="Email" className="input-plain" />
              <input name="phone" value={form.phone} onChange={onChange} placeholder="Phone" className="input-plain" />
              <input name="subject" value={form.subject} onChange={onChange} placeholder="Subject" className="input-plain" />
              <textarea required name="message" value={form.message} onChange={onChange} placeholder="Message" rows={5} className="input-plain md:col-span-2 resize-none" />
            </div>
            <button type="submit" disabled={loading} className="btn-gold mt-5 w-full justify-center">
              {loading ? <><Loader2 size={16} className="animate-spin" /> Sending...</> : <><Send size={14} /> Send Message</>}
            </button>
          </form>
        </div>

        <div className="container-x mt-16">
          <div className="rounded-sm overflow-hidden border border-neutral-200">
            <iframe
              title="Office Location"
              src="https://www.google.com/maps?q=VIT+College+Road+Wadala+East+Mumbai+400037&output=embed"
              className="w-full h-[400px]"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>

      <style>{`
        .input-plain {
          width: 100%;
          border: 1px solid #e5e5e5;
          border-radius: 2px;
          padding: 12px 14px;
          font-size: 14px;
          color: #0e1b34;
          background: #fafafa;
          transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
          outline: none;
        }
        .input-plain:focus {
          border-color: #c9a961;
          box-shadow: 0 0 0 3px rgba(201, 169, 97, 0.15);
          background: #ffffff;
        }
      `}</style>

      <Footer />
    </>
  );
}
